import { useEffect, useState } from "react";
import { Menu, X, Phone } from "lucide-react";
import { company, navLinks } from "@/mocks/home";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 w-full transition-all duration-300 ${
        scrolled || open
          ? "border-b border-background-200/70 bg-background-50/80 backdrop-blur-xl"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-16 w-full items-center justify-between px-4 md:h-20 md:px-6 lg:px-10">
        <a href="#top" className="flex cursor-pointer items-center gap-2.5" aria-label="Porta31 Condomínios - início">
          <img
            src={company.logo}
            alt="Logótipo da Porta31 Condomínios"
            title="Porta31 Condomínios"
            className="h-14 w-auto object-contain md:h-16"
          />
          <span
            className={`font-heading text-base font-bold leading-tight tracking-tight transition-colors duration-300 md:text-lg ${
              scrolled || open ? "text-foreground-950" : "text-background-50"
            }`}
          >
            {company.name}
          </span>
        </a>

        <nav className="hidden items-center gap-8 xl:flex" aria-label="Navegação principal">
          {navLinks.map((link) => (
            <a
              key={link.id}
              href={`#${link.id}`}
              className="group relative cursor-pointer whitespace-nowrap text-sm font-medium text-foreground-700 transition-colors duration-300 hover:text-primary-600"
            >
              {link.label}
              <span className="absolute -bottom-1.5 left-0 h-0.5 w-0 rounded-full bg-primary-500 transition-all duration-300 group-hover:w-full" />
            </a>
          ))}
        </nav>

        <div className="hidden items-center gap-3 xl:flex">
          <a
            href={company.whatsapp}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Ligar para a Porta31 no WhatsApp"
            className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 text-sm font-medium text-foreground-800 transition-colors duration-300 hover:border-primary-400 hover:text-primary-700"
          >
            <Phone className="h-4 w-4" />
            {company.phoneDisplay}
          </a>
          <a
            href="#reunioes"
            className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-all duration-300 hover:scale-[1.03] hover:bg-primary-600 dark:text-foreground-950"
          >
            Agendar Reunião
          </a>
        </div>

        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? "Fechar menu" : "Abrir menu"}
          aria-expanded={open}
          className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-900 transition-colors duration-300 hover:border-primary-400 hover:text-primary-600 xl:hidden"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      <div
        className={`overflow-hidden border-t border-background-200/70 bg-background-50/95 backdrop-blur-xl transition-all duration-300 xl:hidden ${
          open ? "max-h-[520px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <nav className="flex flex-col px-4 py-4 md:px-6" aria-label="Navegação mobile">
          {navLinks.map((link) => (
            <a
              key={link.id}
              href={`#${link.id}`}
              onClick={() => setOpen(false)}
              className="cursor-pointer border-b border-background-200/70 py-3.5 text-base font-medium text-foreground-800 transition-colors duration-300 hover:text-primary-600"
            >
              {link.label}
            </a>
          ))}
          <div className="mt-5 flex flex-col gap-3">
            <a
              href="#reunioes"
              onClick={() => setOpen(false)}
              className="cursor-pointer rounded-md bg-primary-500 py-3 text-center text-sm font-semibold text-background-50 transition-colors duration-300 hover:bg-primary-600 dark:text-foreground-950"
            >
              Agendar Reunião
            </a>
            <a
              href={company.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              className="flex cursor-pointer items-center justify-center gap-2 rounded-md border border-background-300 py-3 text-sm font-medium text-foreground-800 transition-colors duration-300 hover:border-primary-400 hover:text-primary-700"
            >
              <Phone className="h-4 w-4" />
              {company.phoneDisplay}
            </a>
          </div>
        </nav>
      </div>
    </header>
  );
}